import React from "react";
import { Nav, Footer } from "./Nav";
import { Seo } from "./Seo";

const UPDATED = "June 2026";
const CONTACT = "owner@elywellbeing.co.uk";

function LegalShell({ eyebrow, title, testid, children }) {
  return (
    <>
      <Seo title={title} description={`${title} — Ely Wellbeing.`} />
      <Nav />
      <section className="hero" style={{ padding: "9rem 0 2rem" }}>
        <div className="wrap">
          <span className="badge-gold">{eyebrow}</span>
          <h1 style={{ marginTop: "1rem", fontSize: "clamp(2rem,4vw,3rem)" }}>{title}</h1>
          <p className="muted" style={{ marginTop: ".4rem" }}>Last updated: {UPDATED}</p>
        </div>
      </section>
      <section className="sec pad" style={{ paddingTop: 0 }}>
        <div className="wrap legal-body" data-testid={testid} style={{ maxWidth: "72ch", lineHeight: 1.75 }}>
          {children}
        </div>
      </section>
      <Footer />
    </>
  );
}

const H = ({ children }) => <h2 style={{ color: "#fff", marginTop: "2rem", fontSize: "1.25rem" }}>{children}</h2>;
const P = ({ children }) => <p style={{ color: "#cabfae", marginTop: ".75rem" }}>{children}</p>;

export function Terms() {
  return (
    <LegalShell eyebrow="LEGAL" title="Terms & Conditions" testid="terms-page">
      <P>Welcome to Ely Wellbeing ("we", "us", "our"), a wellbeing training centre and therapy hub based in Ely, Cambridgeshire, United Kingdom. By using this website, purchasing a course or therapy, or accessing any of our materials, you agree to these Terms & Conditions.</P>

      <H>1. Our services</H>
      <P>We provide training courses, certifications, live sessions, coaching, therapies and digital products blending modern therapeutic practice with the Wu'sabat path. Course descriptions, durations and prices are shown on the relevant page and may change from time to time.</P>

      <H>2. Enrolment & accounts</H>
      <P>You must create an account to enrol in gated courses. You are responsible for keeping your login details secure and for all activity under your account. Access to course materials is personal to you and may not be shared, resold or redistributed.</P>

      <H>3. Payments</H>
      <P>Payments are processed securely via PayPal. Prices are shown in GBP (£). For pay-in-full course enrolments, payment is taken as a single charge. For the 6-month plan, payment is taken as six monthly instalments and then stops automatically. You authorise us and our payment provider to charge the amounts shown at checkout.</P>

      <H>4. The Passing Guarantee</H>
      <P>Certain courses are offered with a Passing Guarantee. Where advertised, if you enrol on the pay-in-full plan, complete the course and pass the certification assessment, we will refund 100% of your course fee. The guarantee applies only to one-time, pay-in-full enrolments — it does not apply to the 6-month instalment plan. Full details are in our <a className="text-btn" href="/refund-policy">Refund & Guarantee Policy</a>.</P>

      <H>5. Certification</H>
      <P>Certificates are issued on successful completion of the relevant assessment (minimum pass mark as stated in the course). Certificates recognise completion of Ely Wellbeing training and are not a substitute for any statutory or regulated qualification.</P>

      <H>6. Health & wellbeing disclaimer</H>
      <P>Our courses, therapies and content are for education and general wellbeing. They are not medical advice, diagnosis or treatment, and do not replace care from a qualified healthcare professional. Always consult your doctor about any health concern. Herbal products linked from our Herbs page are food supplements and are not intended to prevent, treat or cure any disease.</P>

      <H>7. Affiliate links</H>
      <P>As an Amazon Associate, Ely Wellbeing earns from qualifying purchases. Some product links on our Herbs page open Amazon UK or US and may earn us a small commission at no extra cost to you.</P>

      <H>8. Intellectual property</H>
      <P>All course materials, manuals, audio, diagrams and written content on this site are the property of Ely Wellbeing and are protected by copyright. You may use them for your own study only.</P>

      <H>9. Liability</H>
      <P>To the fullest extent permitted by law, we are not liable for any indirect or consequential loss arising from use of our services. Nothing in these terms limits liability that cannot be limited under UK law.</P>

      <H>10. Governing law</H>
      <P>These terms are governed by the laws of England and Wales, and disputes are subject to the exclusive jurisdiction of the courts of England and Wales.</P>

      <H>11. Contact</H>
      <P>Questions about these terms? Email us at <a className="text-btn" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</P>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell eyebrow="LEGAL" title="Privacy Policy" testid="privacy-page">
      <P>Ely Wellbeing respects your privacy and is committed to protecting your personal data in line with UK GDPR and the Data Protection Act 2018. This policy explains what we collect and how we use it.</P>

      <H>1. Who we are</H>
      <P>Ely Wellbeing, Ely, Cambridgeshire, United Kingdom, is the data controller for the personal data collected through this website. Contact: <a className="text-btn" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</P>

      <H>2. What we collect</H>
      <P>Account details (name, email), authentication data (including Google sign-in where you choose it), purchase and enrolment records, course progress and assessment answers, and technical data such as your IP address and browser type.</P>

      <H>3. How we use it</H>
      <P>To create and manage your account, give you access to purchased courses, issue certificates, process payments, send you transactional emails (such as order confirmations), respond to enquiries, and improve our services. We rely on contract, legitimate interest and your consent as our lawful bases.</P>

      <H>4. Payments</H>
      <P>Payments are handled by PayPal. We do not store your full card or bank details on our servers — these are processed directly by our payment provider under their own privacy terms.</P>

      <H>5. Email</H>
      <P>We send transactional emails (e.g. order confirmations) via our email provider. We will never ask you for your password or payment details by email.</P>

      <H>6. Cookies</H>
      <P>We use essential cookies to keep you signed in and to make the site work. We do not sell your data.</P>

      <H>7. Sharing</H>
      <P>We share data only with service providers who help us run the platform (payments, email, hosting) and where required by law. We do not sell your personal data to third parties.</P>

      <H>8. Retention</H>
      <P>We keep your data for as long as your account is active and as needed to meet legal, accounting and reporting obligations.</P>

      <H>9. Your rights</H>
      <P>You have the right to access, correct, delete or restrict your personal data, to object to processing, and to data portability. To exercise any right, email <a className="text-btn" href={`mailto:${CONTACT}`}>{CONTACT}</a>. You may also complain to the UK Information Commissioner's Office (ico.org.uk).</P>

      <H>10. Contact</H>
      <P>Questions about your privacy? Email <a className="text-btn" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</P>
    </LegalShell>
  );
}

export function RefundPolicy() {
  return (
    <LegalShell eyebrow="LEGAL" title="Refund & Guarantee Policy" testid="refund-page">
      <P>We want you to feel confident enrolling with Ely Wellbeing. This policy explains your refund rights and how our Passing Guarantee works.</P>

      <H>1. Your statutory cancellation right</H>
      <P>Under UK consumer law you normally have 14 days to cancel a purchase for a refund. This right does not apply once you have started, downloaded or accessed digital course content, where you have agreed to immediate access. Live sessions and dated events may have their own cancellation windows shown at booking.</P>

      <H>2. The Passing Guarantee</H>
      <P>Selected courses are offered with a Passing Guarantee. Where it is advertised on the course page and you enrol on the pay-in-full plan, you can earn a full refund of your course fee by:</P>
      <P>• completing the course, and<br />• passing the certification assessment at the required pass mark.</P>
      <P>When you pass, we refund 100% of your paid course fee automatically to your original payment method. In effect, your certification becomes free the moment you earn it.</P>

      <H>3. What the guarantee does not cover</H>
      <P>The Passing Guarantee applies only to one-time, pay-in-full enrolments. It does not apply to the 6-month instalment plan, to memberships, live sessions, therapies, retreats or digital products, unless expressly stated.</P>

      <H>4. Instalment plans</H>
      <P>The 6-month plan is billed monthly for six months and then stops. You can cancel future payments at any time via PayPal, but instalments already taken are non-refundable except where required by law or where a course has not yet been accessed.</P>

      <H>5. How refunds are made</H>
      <P>Refunds are issued to your original PayPal payment. Please allow a few business days for the funds to appear.</P>

      <H>6. Requesting a refund</H>
      <P>To ask about a refund or to raise a concern, email <a className="text-btn" href={`mailto:${CONTACT}`}>{CONTACT}</a> with your name and order details.</P>
    </LegalShell>
  );
}
